import React, { useState, useEffect } from 'react';
import {
    ChevronLeft, ChevronRight, ArrowUpCircle, ArrowDownCircle,
    AlertCircle, Wallet, Info, Download, Loader2
} from 'lucide-react';
import api from '../api/axiosInstance';

const CashFlowCalendar = () => {
    const [viewDate, setViewDate] = useState(new Date());
    const [data, setData] = useState({ events: [], month_name: '', year: '' });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchCalendarData();
    }, [viewDate]);

    const fetchCalendarData = async () => {
        setLoading(true);
        try {
            const month = viewDate.getMonth() + 1;
            const year = viewDate.getFullYear();
            const res = await api.get(`get_cashflow_calendar.php?month=${month}&year=${year}`);
            setData(res.data);
        } catch (err) {
            console.error("Failed to sync calendar", err);
        } finally {
            setLoading(false);
        }
    };

    const changeMonth = (offset) => {
        const nextMonth = new Date(viewDate.setMonth(viewDate.getMonth() + offset));
        setViewDate(new Date(nextMonth));
    };

    // Calculate Totals
    const projectedInflow = data.events.filter(e => e.type === 'in').reduce((a, b) => a + b.amount, 0);
    const scheduledOutflow = data.events.filter(e => e.type === 'out').reduce((a, b) => a + b.amount, 0);

    // Days in Month Logic
    const daysInMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate();
    const firstDayOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1).getDay();

    if (loading) return (
        <div className="h-96 flex flex-col items-center justify-center text-slate-400 font-black italic uppercase tracking-widest">
            <Loader2 className="animate-spin mb-4 text-indigo-600" size={48} />
            Syncing Capital Flow...
        </div>
    );

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            <div className="flex justify-between items-end">
                <div>
                    <h1 className="text-4xl font-black text-slate-900 tracking-tighter italic">Cash Flow Calendar</h1>
                    <p className="text-slate-500 font-medium mt-1">Predicting working capital for {data.month_name} {data.year}</p>
                </div>
                <div className="flex gap-4 p-2 bg-white rounded-2xl border border-slate-100 shadow-sm">
                    <button onClick={() => changeMonth(-1)} className="p-2 hover:bg-slate-50 rounded-xl"><ChevronLeft size={20} /></button>
                    <span className="font-black px-4 flex items-center text-sm uppercase italic">{data.month_name} {data.year}</span>
                    <button onClick={() => changeMonth(1)} className="p-2 hover:bg-slate-50 rounded-xl"><ChevronRight size={20} /></button>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
                {/* CALENDAR GRID */}
                <div className="xl:col-span-3 bg-white rounded-[48px] border border-slate-100 p-10 shadow-sm">
                    <div className="grid grid-cols-7 gap-4 mb-6 text-center">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                            <span key={d} className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{d}</span>
                        ))}
                    </div>

                    <div className="grid grid-cols-7 gap-4">
                        {/* Empty slots for first week */}
                        {Array.from({ length: firstDayOfMonth }).map((_, i) => (
                            <div key={`empty-${i}`} className="min-h-[120px] opacity-20"></div>
                        ))}

                        {Array.from({ length: daysInMonth }).map((_, i) => {
                            const day = i + 1;
                            const dayEvents = data.events.filter(e => e.day === day);

                            return (
                                <div key={day} className="min-h-[120px] bg-slate-50/50 rounded-3xl p-4 border border-slate-50 hover:border-indigo-100 transition-all flex flex-col gap-2 relative group">
                                    <span className="text-xs font-black text-slate-300">{day}</span>
                                    {dayEvents.map((ev, idx) => (
                                        <div key={idx} className={`p-2 rounded-xl text-[9px] font-black uppercase border shadow-sm ${ev.type === 'in' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-rose-50 text-rose-600 border-rose-100'}`}>
                                            <div className="flex justify-between items-center mb-1">
                                                <span className="truncate w-16">{ev.label}</span>
                                                {ev.type === 'in' ? <ArrowUpCircle size={10} /> : <ArrowDownCircle size={10} />}
                                            </div>
                                            <p className="font-black text-[11px]">₹{(ev.amount / 1000).toFixed(1)}K</p>
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* SUMMARY SIDEBAR */}
                <div className="space-y-6">
                    <div className="bg-slate-900 rounded-[40px] p-8 text-white shadow-2xl relative overflow-hidden">
                        <h3 className="text-xl font-black italic mb-8 relative z-10">Monthly Pulse</h3>
                        <div className="space-y-6 relative z-10">
                            <div>
                                <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-1">Expected Inflow</p>
                                <p className="text-3xl font-black">₹{projectedInflow.toLocaleString()}</p>
                            </div>
                            <div>
                                <p className="text-[10px] font-black text-rose-400 uppercase tracking-widest mb-1">Pending Outflow</p>
                                <p className="text-3xl font-black text-rose-400">₹{scheduledOutflow.toLocaleString()}</p>
                            </div>
                            <div className="pt-6 border-t border-white/10">
                                <p className="text-[10px] font-black text-emerald-400 uppercase tracking-widest mb-1">Net Gap/Surplus</p>
                                <p className="text-4xl font-black">₹{(projectedInflow - scheduledOutflow).toLocaleString()}</p>
                            </div>
                        </div>
                        <Wallet size={150} className="absolute -right-10 -bottom-10 text-white/5 rotate-12" />
                    </div>

                    {scheduledOutflow > projectedInflow && (
                        <div className="bg-rose-50 border border-rose-100 rounded-3xl p-6 flex gap-4">
                            <AlertCircle className="text-rose-600 shrink-0" />
                            <p className="text-xs font-bold text-rose-700 leading-relaxed uppercase tracking-tighter">
                                Alert: Potential Cash Crunch. Outflows exceed inflows this month. Review vendor payment dates.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CashFlowCalendar;